import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikProps } from 'formik'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { Divider } from 'primereact/divider'
import { Dropdown, DropdownProps } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { classNames } from 'primereact/utils'

import { useMode } from '../../../../../../../hooks'
import { calculateBreakpointWidth } from '../../../../../../../utils/helper/calculateBreakpointWidth'
import { ConfigurableComponent } from '../../../../../../../utils/wrapper'
import { MasterDataManagementSuppliers } from '../../../../../../ui-config/data'
import { Supplier, useAllSuppliers } from '../../../../../suppliers/data'
import { CompanyCode, CustomFieldDefinition } from '../../../data'
import { FormValues } from '../common'
import { AdditionalProperties } from './AdditionalProperties'
import { AssignedCompanyCodes } from './AssignedCompanyCodes'

interface FormProps {
  formik: FormikProps<FormValues>
  isFormFieldValid: any
  getFormErrorMessage: (name: keyof FormValues) => false | JSX.Element
  getNestedFormErrorMessage: (
    index: number,
    fieldName: string,
  ) => false | JSX.Element
  config: MasterDataManagementSuppliers['edit']
  handleClose: () => void
  isRequestRunning: boolean
  availableCompanyCodesOptions?: CompanyCode[]
  availableCustomFieldsOptions?: CustomFieldDefinition[]
  getAvailableSuppliers: () => Supplier[]
}

export const EditSupplierView: React.FC<FormProps> = ({
  formik,
  isFormFieldValid,
  getFormErrorMessage,
  getNestedFormErrorMessage,
  config,
  handleClose,
  isRequestRunning,
  availableCompanyCodesOptions,
  availableCustomFieldsOptions,
  getAvailableSuppliers,
}) => {
  const { t } = useTranslation()
  const { data: suppliers } = useAllSuppliers()

  const mode = useMode()

  const [isCustomFieldsEditing, setIsCustomFieldsEditing] = useState(false)
  const [isFromDropdownSelection, setIsFromDropdownSelection] = useState(false)

  const companySelectionDropdownRef = React.useRef<any>(null)

  // Company selection
  const companySelectionLabel = <label htmlFor="companySelection"></label>

  // Check if displayId comes from dropdown selection (is it a valid supplier?)
  const isDisplayIdFromDropdown = suppliers?.some(
    s => s.Id === formik.values.displayId,
  )

  // Selected supplier based on displayId
  const selectedSupplier =
    mode === 'add'
      ? isFromDropdownSelection
        ? suppliers?.find(supplier => supplier.Id === formik.values.displayId)
        : undefined
      : suppliers?.find(supplier => supplier.Id === formik.values.displayId)

  // Filter suppliers to show only unused ones + current selected supplier (in edit mode)
  const getAvailableSuppliersWithCurrent = () => {
    const availableSuppliers = getAvailableSuppliers()

    // In info or edit mode, also include the currently selected supplier if it exists
    if (
      (mode === 'info' || mode === 'edit') &&
      selectedSupplier &&
      !availableSuppliers.find(s => s.Id === selectedSupplier.Id)
    ) {
      availableSuppliers.push(selectedSupplier)
    }

    return availableSuppliers
  }

  const selectedSupplierTemplate = (option: Supplier, props: DropdownProps) => {
    if (option) {
      return supplierOptionTemplate(option)
    }

    return <span>{props.placeholder}</span>
  }

  const supplierOptionTemplate = (option: Supplier) => {
    return (
      <span className="text-color">
        {option?.Name} ({option?.Id})
      </span>
    )
  }

  const companySelectionComponent = (
    <Dropdown
      ref={companySelectionDropdownRef}
      id="companySelection"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('companySelection'),
      })}
      value={selectedSupplier}
      options={getAvailableSuppliersWithCurrent()}
      onChange={event => {
        formik.handleChange(event)
        if (!event.value) {
          setIsFromDropdownSelection(false)
          formik.setFieldValue('displayId', '')
          formik.setFieldValue('company', '')
          formik.validateForm()
        } else {
          setIsFromDropdownSelection(true)
          formik.setFieldValue('company', event.value.Name)
          formik.setFieldValue('displayId', event.value.Id)
          formik.validateForm()
        }
      }}
      disabled={isRequestRunning || mode === 'info'}
      optionLabel="Name"
      valueTemplate={selectedSupplierTemplate}
      itemTemplate={supplierOptionTemplate}
      placeholder={t(
        'masterDataManagement.suppliers.table.dialog.form.billingInformation.companySelection.placeholder',
      )}
      showClear
    />
  )

  const companySelectionWrapper = (
    <ConfigurableComponent
      config={config}
      component={companySelectionComponent}
      label={companySelectionLabel}
    />
  )

  // Display id
  const displayIdLabel = (
    <label
      htmlFor="displayId"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('displayId'),
      })}
    ></label>
  )

  const displayIdComponent = (
    <InputText
      id="displayId"
      className="w-full"
      value={formik.values.displayId}
      onChange={event => {
        setIsFromDropdownSelection(false)
        formik.handleChange(event)
      }}
      disabled={
        isRequestRunning ||
        mode === 'info' ||
        (mode === 'add' && isFromDropdownSelection) ||
        (mode === 'edit' && isDisplayIdFromDropdown)
      }
    />
  )

  const displayIdErrorMsg = <div>{getFormErrorMessage('displayId')}</div>

  const displayIdWrapper = (
    <ConfigurableComponent
      config={config}
      component={displayIdComponent}
      label={displayIdLabel}
      errorMsg={displayIdErrorMsg}
    />
  )

  // Comapany
  const companyLabel = (
    <label
      htmlFor="company"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('company'),
      })}
    ></label>
  )

  const companyComponent = (
    <InputText
      id="company"
      className="w-full"
      value={formik.values.company}
      onChange={event => {
        setIsFromDropdownSelection(false)
        formik.handleChange(event)
      }}
      disabled={
        isRequestRunning ||
        mode === 'info' ||
        (mode === 'add' && isFromDropdownSelection) ||
        (mode === 'edit' && isDisplayIdFromDropdown)
      }
    />
  )

  const companyErrorMsg = <div>{getFormErrorMessage('company')}</div>

  const companyWrapper = (
    <ConfigurableComponent
      config={config}
      component={companyComponent}
      label={companyLabel}
      errorMsg={companyErrorMsg}
    />
  )

  // Tax Number
  const taxNumberLabel = (
    <label
      htmlFor="taxNumber"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('taxNumber'),
      })}
    ></label>
  )

  const taxNumberComponent = (
    <InputText
      id="taxNumber"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('taxNumber'),
      })}
      value={formik.values.taxNumber}
      onChange={formik.handleChange}
      disabled={isRequestRunning || mode === 'info'}
    />
  )

  const taxNumberErrorMsg = <div>{getFormErrorMessage('taxNumber')}</div>

  const taxNumberWrapper = (
    <ConfigurableComponent
      config={config}
      component={taxNumberComponent}
      label={taxNumberLabel}
      errorMsg={taxNumberErrorMsg}
    />
  )

  // Vat id
  const salesTaxIDLabel = (
    <label
      htmlFor="salesTaxID"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('salesTaxID'),
      })}
    ></label>
  )

  const salesTaxIDComponent = (
    <InputText
      id="salesTaxID"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('salesTaxID'),
      })}
      value={formik.values.salesTaxID}
      onChange={formik.handleChange}
      disabled={isRequestRunning || mode === 'info'}
    />
  )

  const salesTaxIDErrorMsg = <div>{getFormErrorMessage('salesTaxID')}</div>

  const salesTaxIDWrapper = (
    <ConfigurableComponent
      config={config}
      component={salesTaxIDComponent}
      label={salesTaxIDLabel}
      errorMsg={salesTaxIDErrorMsg}
    />
  )

  // Street
  const streetLabel = (
    <label
      htmlFor="street"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('street'),
      })}
    ></label>
  )

  const streetComponent = (
    <InputText
      id="street"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('street'),
      })}
      value={formik.values.street}
      onChange={formik.handleChange}
      disabled={isRequestRunning || mode === 'info'}
    />
  )

  const streetErrorMsg = <div>{getFormErrorMessage('street')}</div>

  const streetWrapper = (
    <ConfigurableComponent
      config={config}
      component={streetComponent}
      label={streetLabel}
      errorMsg={streetErrorMsg}
    />
  )

  // Postal Code
  const postalCodeLabel = (
    <label
      htmlFor="postalCode"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('postalCode'),
      })}
    ></label>
  )

  const postalCodeComponent = (
    <InputText
      id="postalCode"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('postalCode'),
      })}
      value={formik.values.postalCode}
      onChange={formik.handleChange}
      disabled={isRequestRunning || mode === 'info'}
    />
  )

  const postalCodeErrorMsg = <div>{getFormErrorMessage('postalCode')}</div>

  const postalCodeWrapper = (
    <ConfigurableComponent
      config={config}
      component={postalCodeComponent}
      label={postalCodeLabel}
      errorMsg={postalCodeErrorMsg}
    />
  )

  // City
  const cityLabel = (
    <label
      htmlFor="city"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('city'),
      })}
    ></label>
  )

  const cityComponent = (
    <InputText
      id="city"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('city'),
      })}
      value={formik.values.city}
      onChange={formik.handleChange}
      disabled={isRequestRunning || mode === 'info'}
    />
  )

  const cityErrorMsg = <div>{getFormErrorMessage('city')}</div>

  const cityWrapper = (
    <ConfigurableComponent
      config={config}
      component={cityComponent}
      label={cityLabel}
      errorMsg={cityErrorMsg}
    />
  )

  // Country
  const countryLabel = (
    <label
      htmlFor="country"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('country'),
      })}
    ></label>
  )

  const countryComponent = (
    <InputText
      id="country"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('country'),
      })}
      value={formik.values.country}
      onChange={formik.handleChange}
      disabled
    />
  )

  const countryErrorMsg = <div>{getFormErrorMessage('country')}</div>

  const countryWrapper = (
    <ConfigurableComponent
      config={config}
      component={countryComponent}
      label={countryLabel}
      errorMsg={countryErrorMsg}
    />
  )

  // Footer
  const footer = () => {
    if (mode === 'info')
      return (
        <Button
          label={t('masterDataManagement.suppliers.table.dialog.close.label')}
          icon="pi pi-times"
          className="p-button-text w-full md:w-auto mb-1 md:mb-0"
          onClick={handleClose}
          disabled={isRequestRunning}
        />
      )

    const saveButtonLabel =
      mode === 'add'
        ? t('masterDataManagement.suppliers.table.dialog.add.label')
        : t('masterDataManagement.suppliers.table.dialog.save.label')

    return (
      <>
        <Button
          label={t('masterDataManagement.suppliers.table.dialog.cancel.label')}
          icon="pi pi-times"
          className="p-button-text w-full md:w-auto mb-1 md:mb-0"
          onClick={handleClose}
          disabled={isRequestRunning}
        />
        <Button
          label={saveButtonLabel}
          icon={'pi pi-check'}
          className="w-full md:w-auto"
          onClick={formik.submitForm}
          type="submit"
          disabled={!formik.dirty || isCustomFieldsEditing}
          loading={isRequestRunning}
        />
      </>
    )
  }

  const header = () => {
    if (mode === 'add')
      return t('masterDataManagement.suppliers.table.dialog.title.add')

    if (mode === 'edit')
      return t('masterDataManagement.suppliers.table.dialog.title.edit', {
        company: formik.values.company,
      })

    if (mode === 'info')
      return t('masterDataManagement.suppliers.table.dialog.title.info', {
        company: formik.values.company,
      })
  }

  return (
    <Dialog
      header={header}
      visible
      style={{ width: '55rem' }}
      breakpoints={{ [calculateBreakpointWidth(55)]: '90vw' }}
      footer={footer}
      onHide={handleClose}
      blockScroll
    >
      <div className="formgrid grid">
        <h4 className="col-12">
          {t(
            'masterDataManagement.suppliers.table.dialog.form.billingInformation.title',
          )}
        </h4>

        {mode !== 'info' && (
          <div className="col-12 ">{companySelectionWrapper}</div>
        )}

        <div className="col-12 md:col-3">{displayIdWrapper}</div>

        <div className="col-12 md:col-9">{companyWrapper}</div>

        <div className="col-12 md:col-6">{taxNumberWrapper}</div>

        <div className="col-12 md:col-6">{salesTaxIDWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      <div className="formgrid grid">
        <h4 className="col-12">
          {t(
            'masterDataManagement.suppliers.table.dialog.form.billingAddress.title',
          )}
        </h4>

        <div className="col-12">{streetWrapper}</div>

        <div className="col-12 md:col-3">{postalCodeWrapper}</div>

        <div className="col-12 md:col-6">{cityWrapper}</div>

        <div className="col-12 md:col-3">{countryWrapper}</div>
      </div>

      <div className="formgrid grid">
        <h4 className="col-12">
          {t(
            'masterDataManagement.suppliers.table.dialog.form.assignedCompanyCodes.title',
          )}
        </h4>
        <AssignedCompanyCodes
          formik={formik}
          config={config.assignedCompanyCodes}
          getFormErrorMessage={getFormErrorMessage}
          getNestedFormErrorMessage={getNestedFormErrorMessage}
          availableCompanyCodesOptions={availableCompanyCodesOptions}
          onEditingModeChange={setIsCustomFieldsEditing}
        />
      </div>

      <div className="formgrid grid">
        <h4 className="col-12">
          {t(
            'masterDataManagement.suppliers.table.dialog.form.additionalProperties.title',
          )}
        </h4>
        <AdditionalProperties
          formik={formik}
          config={config.additionalProperties}
          getNestedFormErrorMessage={getNestedFormErrorMessage}
          availableCustomFieldsOptions={availableCustomFieldsOptions}
          onEditingModeChange={setIsCustomFieldsEditing}
        />
      </div>
    </Dialog>
  )
}
